/**
 * Feedbacks — duas abas na mesma tela: o que os clientes escrevem pelo botão
 * do site novo, e o que a equipe escreve pelo botão flutuante do Sistema
 * (`FeedbackDaEquipe`). A aba vai na URL (`?aba=equipe`) para o link servir.
 *
 * O resto deste cabeçalho é da aba dos clientes.
 *
 * Feedback do novo Diagweb — a fila de triagem.
 *
 * **Abre como o sistema antigo abre**: últimos 30 dias, sem status escolhido.
 * A tela já abriu filtrada em "A fazer", que era melhor como fila de trabalho —
 * mas a decisão foi portar fiel, e o filtro continua a um clique de distância.
 *
 * **A troca de status é otimista, e a tela participa disso.** Ao trocar, ela
 * manda junto o status que está mostrando. Se o feedback já não estiver nele, a
 * API responde 409 com o status que vale agora — e a tela se corrige sozinha, em
 * vez de sobrescrever o trabalho de quem mexeu antes. Duas pessoas com a mesma
 * lista aberta é o caso normal aqui, não o excepcional.
 *
 * **Feedback sem status vale "A fazer".** São 18 dos 73 — nunca foram tocados, e
 * são justamente os que a fila precisa mostrar primeiro.
 */

import { useEffect, useState, type ReactNode } from "react"
import { useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import {
  baixarCsvDeFeedback, conflitoDe, historicoDeFeedback, listarStatusDeFeedback, listarTiposDeFeedback, trocarStatusDeFeedback,
  urlDaImagemDeFeedback,
} from "@/api/feedback"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import { Botao, Secao, estiloCampo, mensagem } from "../outros/pecas"
import { AbasDoFeedback, type AbaDoFeedback } from "./AbasDoFeedback"
import { FeedbackDaEquipe } from "./FeedbackDaEquipe"

type Card = {
  feedbackId: number
  tipoCodigo: string
  tipoNome: string
  clienteId: number
  mensagem: string | null
  dataHora: string
  quantidadeImagens: number
  statusCodigo: string
  statusNome: string
  /** Nomes dos arquivos: cada foto vem de `urlDaImagemDeFeedback`. */
  imagens: string[]
}

type Dados = {
  indicadores: { total: number; gostei: number; naoGostei: number; ideia: number; comImagens: number }
  feedbacks: Card[]
  paginacao: { paginaAtual: number; totalPaginas: number }
}

function iso(d: Date) { return d.toISOString().slice(0, 10) }

/**
 * Os últimos 30 dias — o mesmo recorte com que o `RelatorioFeedback/Index` do
 * sistema antigo abre (`DateTime.Today.AddDays(-29)` até hoje).
 */
function trintaDias() {
  const ate = new Date()
  const de = new Date()
  de.setDate(ate.getDate() - 29)
  return { de: iso(de), ate: iso(ate) }
}

export function Feedback() {
  const [busca, setBusca] = useSearchParams()
  const aba: AbaDoFeedback = busca.get("aba") === "equipe" ? "equipe" : "clientes"
  const abas = (
    <AbasDoFeedback
      aba={aba}
      aoTrocar={(a) => setBusca(a === "equipe" ? { aba: "equipe" } : {}, { replace: true })}
    />
  )

  return aba === "equipe" ? <FeedbackDaEquipe abas={abas} /> : <FeedbackDosClientes abas={abas} />
}

function FeedbackDosClientes({ abas }: { abas: ReactNode }) {
  const qc = useQueryClient()
  const [periodo, setPeriodo] = useState(trintaDias)
  // Sem status escolhido, como o sistema antigo abre. A fila do "A fazer"
  // continua a um clique; o que mudou foi só por onde a tela começa.
  const [status, setStatus] = useState<string>("")
  const [tipo, setTipo] = useState("")
  const [pagina, setPagina] = useState(1)
  const [aberto, setAberto] = useState<number | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const catalogo = useQuery({
    queryKey: ["feedback-status"] as const,
    queryFn: listarStatusDeFeedback,
    staleTime: Infinity,
  })

  /** Os tipos do botão do site. Vêm da API pelo mesmo motivo dos status. */
  const tipos = useQuery({
    queryKey: ["feedback-tipos"] as const,
    queryFn: listarTiposDeFeedback,
    staleTime: Infinity,
  })

  const chave = ["feedback-fila", periodo.de, periodo.ate, status, tipo, pagina] as const
  const dados = useQuery({
    queryKey: chave,
    queryFn: () => obter<Dados>("api/relatorios/feedback", {
      dataInicio: periodo.de,
      dataFim: periodo.ate,
      statusCodigos: status ? [status] : undefined,
      tipoCodigos: tipo ? [tipo] : undefined,
      pagina,
    }),
  })

  const historico = useQuery({
    queryKey: ["feedback-historico", aberto] as const,
    queryFn: () => historicoDeFeedback(aberto!),
    enabled: aberto != null,
  })

  const trocar = useMutation({
    mutationFn: ({ id, codigo, esperado }: { id: number; codigo: string; esperado: string }) =>
      trocarStatusDeFeedback(id, codigo, esperado),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ["feedback-fila"] })
      qc.invalidateQueries({ queryKey: ["feedback-historico", v.id] })
      setRecado(r.mensagem)
    },
    onError: (e) => {
      const c = conflitoDe(e)
      // A tela se corrige: recarrega e diz o que vale agora, em vez de insistir.
      if (c) {
        qc.invalidateQueries({ queryKey: ["feedback-fila"] })
        qc.invalidateQueries({ queryKey: ["feedback-historico"] })
        setRecado(`Outra pessoa mudou este feedback antes: agora está em "${c.nomeAtual}". A lista foi recarregada.`)
        return
      }
      setRecado(mensagem(e))
    },
  })

  const cards = dados.data?.feedbacks ?? []
  const total = dados.data?.indicadores.total ?? 0
  const paginas = dados.data?.paginacao.totalPaginas ?? 0

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo={abas}
        detalhe={dados.data
          ? `${total} no filtro`
          : "O que os clientes escrevem pelo botão do site novo"}
      >
        <input type="date" value={periodo.de}
               onChange={(e) => { setPeriodo({ ...periodo, de: e.target.value }); setPagina(1) }}
               style={{ ...estiloCampo, width: 140 }} />
        <input type="date" value={periodo.ate}
               onChange={(e) => { setPeriodo({ ...periodo, ate: e.target.value }); setPagina(1) }}
               style={{ ...estiloCampo, width: 140 }} />

        <select value={status} onChange={(e) => { setStatus(e.target.value); setPagina(1) }}
                style={{ ...estiloCampo, width: 170 }}>
          <option value="">Todos os status</option>
          {(catalogo.data ?? []).map((s) => (
            <option key={s.codigo} value={s.codigo}>{s.nome}</option>
          ))}
        </select>

        <select value={tipo} onChange={(e) => { setTipo(e.target.value); setPagina(1) }}
                style={{ ...estiloCampo, width: 170 }}>
          <option value="">Todos os tipos</option>
          {(tipos.data ?? []).map((t) => (
            <option key={t.codigo} value={t.codigo}>{t.nome}</option>
          ))}
        </select>

        <Botao
          aoClicar={() => baixarCsvDeFeedback({
            dataInicio: periodo.de, dataFim: periodo.ate,
            statusCodigos: status ? [status] : undefined,
            tipoCodigos: tipo ? [tipo] : undefined,
          })}
          titulo="CSV com BOM e proteção contra fórmula — a mensagem é escrita pelo cliente"
        >
          Exportar CSV
        </Botao>
      </Faixa>

      <AvisoDeDadoGlobal oQueMuda="Trocar o status é uma escrita global: o feedback não pertence a projeto nenhum" />

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: 14 }}>
          {dados.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Carregando…</p>
          ) : dados.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(dados.error)}</p>
          ) : cards.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)", maxWidth: "56ch", lineHeight: 1.6 }}>
              Nada neste filtro. {status === "todo" && "Fila limpa — nenhum feedback esperando triagem."}
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {cards.map((c) => (
                <CartaoDeFeedback
                  key={c.feedbackId}
                  c={c}
                  aberto={aberto === c.feedbackId}
                  aoAbrir={() => setAberto(aberto === c.feedbackId ? null : c.feedbackId)}
                  status={catalogo.data ?? []}
                  trocando={trocar.isPending}
                  aoTrocar={(codigo) =>
                    trocar.mutate({ id: c.feedbackId, codigo, esperado: c.statusCodigo })}
                />
              ))}
            </div>
          )}

          {paginas > 1 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14 }}>
              <Botao aoClicar={() => setPagina((p) => p - 1)} desligado={pagina <= 1}>Anterior</Botao>
              <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
                página {pagina} de {paginas}
              </span>
              <Botao aoClicar={() => setPagina((p) => p + 1)} desligado={pagina >= paginas}>Próxima</Botao>
            </div>
          )}
        </div>

        {aberto != null && (
          <aside style={{
            width: 380, flex: "0 0 380px", borderLeft: "1px solid var(--line2)",
            overflowY: "auto", padding: 16, background: "var(--surface)",
          }}>
            <Secao titulo={`Histórico do feedback ${aberto}`}>
              {historico.isPending ? (
                <p style={{ fontSize: 12, color: "var(--txt3)" }}>Carregando…</p>
              ) : historico.error ? (
                <p style={{ fontSize: 12, color: "var(--erro)" }}>{mensagem(historico.error)}</p>
              ) : historico.data?.trocas.length === 0 ? (
                <p style={{ fontSize: 12, color: "var(--txt3)" }}>
                  Nunca foi triado — está em "{historico.data.statusAtual}" por omissão.
                </p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: 1.7 }}>
                  {(historico.data?.trocas ?? []).map((t) => (
                    <li key={t.id}>
                      <strong>{t.de}</strong> → <strong>{t.para}</strong>
                      <br />
                      <span style={{ color: "var(--txt3)" }}>
                        {new Date(t.quando).toLocaleString("pt-BR")} · {t.administrador}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Secao>
          </aside>
        )}
      </div>

      <BarraDeEstado direita={dados.data ? `${total} feedback(s) no filtro` : undefined}>
        {recado ?? "Trocar o status manda junto o que a tela mostra — se alguém mudou antes, a lista se corrige."}
      </BarraDeEstado>
    </div>
  )
}

function CartaoDeFeedback({ c, aberto, aoAbrir, status, trocando, aoTrocar }: {
  c: Card
  aberto: boolean
  aoAbrir: () => void
  status: { codigo: string; nome: string }[]
  trocando: boolean
  aoTrocar: (codigo: string) => void
}) {
  return (
    <div style={{
      border: `1px solid ${aberto ? "var(--brand)" : "var(--line2)"}`,
      borderRadius: 9, background: "var(--surface)", padding: "10px 12px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6, flexWrap: "wrap" }}>
        <span className="mono" style={{ fontSize: 11, color: "var(--txt3)" }}>#{c.feedbackId}</span>
        <span style={{
          padding: "1px 7px", borderRadius: 5, fontSize: 11, background: "var(--surface3)",
        }}>
          {c.tipoNome}
        </span>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>{c.dataHora}</span>
        <span style={{ fontSize: 11, color: "var(--txt3)" }}>cliente {c.clienteId}</span>
        {c.quantidadeImagens > 0 && (
          <span style={{ fontSize: 11, color: "var(--txt3)" }}>{c.quantidadeImagens} imagem(ns)</span>
        )}

        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 7 }}>
          <select
            value={c.statusCodigo}
            disabled={trocando}
            onChange={(e) => aoTrocar(e.target.value)}
            style={{ ...estiloCampo, width: 150, padding: "3px 6px" }}
          >
            {status.map((s) => <option key={s.codigo} value={s.codigo}>{s.nome}</option>)}
          </select>
          <Botao aoClicar={aoAbrir}>{aberto ? "Fechar" : "Histórico"}</Botao>
        </span>
      </div>

      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
        {c.mensagem || <span style={{ color: "var(--txt3)" }}>(sem mensagem)</span>}
      </p>

      {c.imagens.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {c.imagens.map((nome) => <FotoDoFeedback key={nome} nome={nome} />)}
        </div>
      )}
    </div>
  )
}

/**
 * A miniatura de uma foto do feedback, baixada com o token.
 *
 * O clique abre a foto no tamanho original numa aba nova. A aba é aberta antes
 * do download terminar porque o navegador bloqueia `window.open` que não sai
 * direto do clique.
 */
function FotoDoFeedback({ nome }: { nome: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    let vivo = true
    let criada: string | null = null
    setUrl(null)
    setFalhou(false)
    urlDaImagemDeFeedback(nome, 240)
      .then((u) => { criada = u; if (vivo) setUrl(u); else URL.revokeObjectURL(u) })
      .catch(() => { if (vivo) setFalhou(true) })
    return () => { vivo = false; if (criada) URL.revokeObjectURL(criada) }
  }, [nome])

  const abrir = () => {
    const aba = window.open("", "_blank")
    if (!aba) return
    urlDaImagemDeFeedback(nome)
      .then((u) => { aba.location.href = u })
      .catch(() => aba.close())
  }

  const estilo = { height: 64, minWidth: 64, borderRadius: 5, border: "1px solid var(--line2)" }

  if (falhou) {
    return (
      <span title={`Foto indisponível: ${nome}`} style={{
        ...estilo, display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 10.5, color: "var(--txt3)", padding: "0 8px",
      }}>
        indisponível
      </span>
    )
  }

  return (
    <button type="button" onClick={abrir} title={nome}
      style={{ padding: 0, border: 0, background: "none", cursor: "pointer" }}>
      {url
        ? <img src={url} alt="" style={estilo} />
        : <span style={{ ...estilo, display: "inline-block", background: "var(--surface2)" }} />}
    </button>
  )
}
